import { SimpleTable } from '@tassat/ag-grid';
import { SubmitButton } from '@tassat/button';
import { Transaction } from '@tassat/fred-definitions';
import React from 'react';
import ReactGA from 'react-ga4';

import history from 'Lib/browserHistory';
import { auditTimeSort } from 'Lib/stringHelpers';
import Worker from 'Lib/worker/PrimusWorkerWrapper';
import { useAppSelector } from 'Reducers/store/hooks';
import { getAssets, getUser } from 'Selectors/common';
import { clientCompletedTransactionsMapping, clientPendingTransactionsMapping } from 'Selectors/transactions';
import FormatAssetsAmount from 'SharedComponents/cellRenderComponent/formatAssetsAmount';
import TransactionAccountLinkCellRenderer from 'SharedComponents/cellRenderComponent/transactionAccountLinkCellRenderer';
import TransactionAssetCellRenderer from 'SharedComponents/cellRenderComponent/transactionAssetCellRenderer';
import { TransactionTypeConversion } from 'SharedComponents/cellRenderComponent/transactionTypes';

import { getColumnDefs } from './pendingTransactionsGridOptions';
import { muiStyles } from './styles';
import { PendingTransactionsTableProps } from './types';

export const PendingTransactionsTable: React.FC<PendingTransactionsTableProps> = (props) => {
    const { transactionFilter } = props;

    const classes = muiStyles(props);
    const user = useAppSelector(getUser);
    const assets = useAppSelector((state) => getAssets(state));
    const transactions = useAppSelector((state) => {
        let transactions = [] as Transaction[];
        if (transactionFilter === 'pending') {
            transactions = clientPendingTransactionsMapping(state);
        } else if (transactionFilter === 'recent') {
            transactions = clientCompletedTransactionsMapping(state);
        }

        return transactions;
    });
    const [ sortedTransactions, setSortedTransactions ] = React.useState<Transaction[]>(transactions);

    const fetchTransactions = async (firmId: string) => {
        let res = [];
        let isCompleted = false;
        if (transactionFilter === 'recent') {
            isCompleted = true;
        }
        const transactionQuery = {
            Condition: `firmId|EQ|${firmId}:isCompleted|EQ|${isCompleted}:${!isCompleted ? 'status|NEQ|6' : ''}`,
            Sort: 'auditTime|DESC',
            PageNo: 1,
            RecordPerPage: 4
        };

        if (transactionFilter === 'pending') {
            res = await Worker.getInstance()
                .getPendingTransactions(transactionQuery) || { data: undefined };
        } else if (transactionFilter === 'recent') {
            res = await Worker.getInstance()
                .getTransactions(transactionQuery) || { data: undefined };
        }

        return res;
    };

    const handleSeeDetails = () => {
        let completed = false;
        if (transactionFilter === 'recent') {
            completed = true;
        }
        ReactGA.event('click', {
            event_category: 'engagement',
            event_label: transactionFilter === 'recent' ? 'recent_transactions_show_all' : 'pending_transactions_show_all'
        });
        history.push('/accounts/history', { tableFilters: { IsCompleted: completed }});
    };

    React.useEffect(() => {
        if (!user) return;

        fetchTransactions(user.FirmId);
    }, [user]);

    React.useEffect(() => {
        const mappedTransactions = transactions;

        mappedTransactions.sort(auditTimeSort);

        setSortedTransactions(mappedTransactions);
    }, [transactions]);

    const tableDetails = React.useMemo(() => {
        if (transactionFilter === 'pending') {
            return {
                title: 'Pending Transactions',
                noRows: 'No pending transactions',
                columnDefs: getColumnDefs(transactionFilter)
            };
        } else if (transactionFilter === 'recent') {
            return {
                title: 'Recent Transactions',
                noRows: 'No recent transactions',
                columnDefs: getColumnDefs(transactionFilter)
            };
        }

        return {
            title: 'Transactions',
            noRows: 'No transactions',
            columnDefs: []
        };
    }, [transactionFilter]);

    return (
        <>
            <SimpleTable
                columnDefs={tableDetails.columnDefs}
                components={{
                    formatAssetsAmount: FormatAssetsAmount,
                    accountLink: TransactionAccountLinkCellRenderer,
                    transactionAsset: TransactionAssetCellRenderer,
                    transType: TransactionTypeConversion
                }}
                context={{ assets: structuredClone(assets) }}
                getRowId={({ data }) => `${data.RequestId}-${data.type}`}
                minHeight={140}
                overlayNoRowsTemplate={tableDetails.noRows}
                rowData={sortedTransactions}
                title={tableDetails.title}
            />
            <div className={classes.seeDetailsButtonContainer}>
                <SubmitButton
                    className={classes.seeDetailsButton}
                    onClick={handleSeeDetails}
                    text='Show All'
                    type='submit'
                />
            </div>
        </>
    );
};